import { type ChangeEvent, useRef, useState } from 'react';

import { faFileAudio, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components';

import { createDebugLogger } from '@videa/utils';

const debug = createDebugLogger('AudioFileUpload');

// Supported audio formats for upload
const ACCEPTED_AUDIO_TYPES = [
  'audio/webm',
  'audio/mp3',
  'audio/mpeg',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/m4a',
  'audio/mp4',
  'audio/ogg',
  'audio/flac',
];

const ACCEPTED_EXTENSIONS = '.webm,.mp3,.wav,.m4a,.mp4,.ogg,.flac';

// Maximum file size: 500MB
const MAX_FILE_SIZE_MB = 500;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface AudioFileUploadProps {
  onFileSelected: (file: File, blobUrl: string) => void;
  isDisabled?: boolean;
  size?: 'default' | 'sm';
  buttonStyling?: string;
}

export const AudioFileUpload = ({
  onFileSelected,
  isDisabled = false,
  size = 'default',
  buttonStyling = '',
}: AudioFileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset any previous error
    setError(null);
    setIsProcessing(true);

    try {
      // Validate file type
      if (!ACCEPTED_AUDIO_TYPES.includes(file.type)) {
        throw new Error(
          'Unsupported audio format. Please upload a WebM, MP3, WAV, M4A, or OGG file.',
        );
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new Error(
          `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please upload a smaller file.`,
        );
      }

      // Create a blob URL for the file
      const blobUrl = URL.createObjectURL(file);

      // Pass the file and blob URL to the parent
      onFileSelected(file, blobUrl);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to process audio file';
      setError(errorMessage);
      debug.error('Audio file upload error:', err);
    } finally {
      setIsProcessing(false);
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="vai-flex vai-flex-col vai-items-center">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleFileChange}
        className="vai-hidden"
        aria-label="Upload audio file"
      />
      <Button
        variant="outline"
        size={size}
        onClick={handleButtonClick}
        disabled={isDisabled || isProcessing}
        className={buttonStyling}
      >
        {isProcessing ? (
          <span>
            <FontAwesomeIcon icon={faSpinner} spin className="vai-mr-1" />
            Processing...
          </span>
        ) : (
          <span>
            <FontAwesomeIcon icon={faFileAudio} className="vai-mr-1" />
            Upload Audio File
          </span>
        )}
      </Button>
      {error && (
        <p className="vai-text-sm vai-text-red-500 vai-mt-2 vai-text-center">
          {error}
        </p>
      )}
    </div>
  );
};
